"use client"
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Body, Heading } from "@/client/foundations/Typography";
// import { Box, Stack } from "@fastlooptech/layout-components";
import classNames from "classnames";
import { spaceUnit } from "@/helpers/utils.helper";
// import { useResponsive } from "@/client/hooks/useResponsive";
import { Box, Stack } from "@/layouts/LayoutComponents";
// import "./Carousel.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dynamic from "next/dynamic";
import { MdOutlineNavigateNext, MdOutlineNavigateBefore } from "react-icons/md";
import { useResponsive } from "@/client/hooks/useResponsive";
const Slider = dynamic(() => import("react-slick"), {
  ssr: false, // Disable server-side rendering for this component
});

// import Carousel from "react-multi-carousel";
// import 'react-multi-carousel/lib/styles.css';

export interface CarouselProps {
  "data-test-id"?: string;
  /**
   * The Carousel className.
   * @type {string}
   */
  className?: string;
  /**
   * The subtitle of the Carousel.
   * @type string
   * @optional
   */
  subtitle?: string;
  /**
   * The description of the Carousel.
   * @type string
   * @optional
   */
  description?: string;
  /**
   * The content of the Carousel.
   * @type string | ReactNode;
   * @required
   */
  children: ReactNode;
  /**
   * Enable animation triggered whe the element is visible.
   * @type boolean
   * @optional
   */
  animated?: boolean;
  /**
   * The animation type triggered when the element is visible. 
   * @type string
   * @optional
   */
  animation?: string;
  /**
   * The autoplay animation of the Carousel.
   * @type boolean
   * @optional
   */
  autoplay?: boolean;
  autoscroll?: boolean;

  swipeToSlide?: boolean;
  draggable?: boolean;

  /**
   * The slidesToShow of the Carousel.
   * @type string
   * @optional
   */
  slidesToShow?: number;

  /**
   * The slidesToScroll of the Carousel.
   * @type string
   * @optional
   */
  slidesToScroll?: number;

  /**
   * The autoplaySpeed of the Carousel.
   * @type string
   * @optional
   */
  autoplaySpeed?: number;

  /**
   * The speed of the Carousel.
   * @type string
   * @optional
   */
  speed?: number;
  id?: string;
  transitionType?: string;
  centerMode?: boolean;
  infinite?: boolean;
  showPaging?: boolean;
  showButtons?: boolean;
  pauseOnHover?: boolean;
  variableWidth?: boolean;
}

export function CarouselComponent(props: CarouselProps) {
  const [sliderSettings, setSliderSettings] = useState({});
  const { isSm, isMd } = useResponsive();

  useEffect(() => {
    const tempSliderSettings = {
      ...(props?.transitionType === "fade" && { fade: true }),
      dots: props?.showPaging,

      // For variable width
      ...(props?.centerMode && {
        className: "center slider variable-width",
        centerMode: true,
        // centerPadding: "60px",
        variableWidth: true,
      }),

      ...(!!props?.showButtons && {
        nextArrow: <CarouselNextArrow />,
        prevArrow: <CarouselPrevArrow />
      }),

      "infinite": props?.infinite,
      "dotsClass": "slick-dots slick-thumb",
      "autoplay": !!props?.autoplay,
      "autoplaySpeed": Number(props?.autoplaySpeed),
      "cssEase": props?.animation || "ease",
      "speed": Number(props?.speed),
      "slidesToShow": Number(props?.slidesToShow),
      "slidesToScroll": Number(props?.slidesToScroll),
      "pauseOnHover": !!props?.pauseOnHover,
      "swipeToSlide": !!props?.swipeToSlide,
      "draggable": !!props?.draggable,
      "variableWidth": !!props?.variableWidth,
      "lazyLoad": "progressive",
      // responsive behaviour to be set via breakpoints
      // responsive: [
      //   {
      //     breakpoint: 4000,
      //     settings: {
      //       slidesToShow: Number(props?.slidesToShow) || 3,
      //       slidesToScroll: 1,
      //       infinite: true,
      //       dots: true,
      //     },
      //   },
      //   {
      //     breakpoint: 1024,
      //     settings: {
      //       slidesToShow: Number(props?.slidesToShow) || 3,
      //       slidesToScroll: 1,
      //       infinite: true,
      //       dots: true,
      //     },
      //   },
      //   {
      //     breakpoint: 600,
      //     settings: {
      //       slidesToShow: 2,
      //       slidesToScroll: 1,
      //       initialSlide: 1,
      //     },
      //   },
      //   {
      //     breakpoint: 480,
      //     settings: { slidesToShow: 1, slidesToScroll: 1 },
      //   },
      // ],
    }

    setSliderSettings({ ...sliderSettings, ...tempSliderSettings })
  }, [
    props?.transitionType,
    props?.animation,
    props?.autoplay,
    props?.autoplaySpeed,
    props?.slidesToScroll,
    props?.slidesToShow,
    props?.speed,
    props?.swipeToSlide,
    props?.centerMode
  ]);

  const renderCarouselIntro = useMemo(() => {
    if (props.subtitle || props.description) {
      return (
        <Box className="w-screen max-w-[1440px] m-auto">
          <Stack gap={spaceUnit * 3} width={isSm ? 540 : "auto"} column>
            <Heading size="l" animated={props.animated ?? false} animation={props.animation ?? ""}>
              {props.subtitle || ""}
            </Heading>
            <Body size="m" animated={props.animated ?? false} animation={props.animation ?? ""}>
              {props.description || ""}
            </Body>
          </Stack>
        </Box>
      );
    }
  }, [isSm, props.animated, props.animation, props.description, props.subtitle]);


  function CarouselNextArrow(props: {
    className?: string;
    style?: React.CSSProperties;
    onClick?: () => void;
  }) {
    const { className, style, onClick } = props;
    return (
      <div
        className={classNames(className)}
        style={{ ...style, right: "24px", backgroundColor:"white" }}
        onClick={onClick}
      >
        <MdOutlineNavigateNext className="w-[30px] h-[30px]" />
      </div>
    );
  }

  function CarouselPrevArrow(props: any) {
    const { className, style, onClick } = props;
    return (
      <div
        className={classNames(className)}
        style={{ ...style, left: "24px", backgroundColor:"white" }}
        onClick={onClick}
      >
        <MdOutlineNavigateBefore className="w-[30px] h-[30px]" />
      </div>
    );
  }

  return (
    <Box column className={`w-full carouselWrapper overflow-x-hidden${props?.showPaging ? " pb-[30px]" : ""}`}>
      <Box className="px-2">{renderCarouselIntro}</Box>
      <div className="w-full custom-slick-carousel">
        {Object.entries(sliderSettings).length > 0 && <Slider {...sliderSettings}>{props?.children}</Slider>}
      </div>
    </Box>
  );
}
